// tslint:disable: no-magic-numbers
// tslint:disable: typedef
// tslint:disable: align
import React from 'react';
import { Grid, Paper, withStyles } from '@material-ui/core';
import styles from './DashboardLayout.styles';
import IDashboardLayoutProperties, { IDashboardLayoutHandle } from './DashboardLayout.properties';
import { ActionBarContext } from 'utils/ActionBarContextProvider';
import { BACK_BUTTON } from 'components/common/ActionBar/constants';
import DragIcon from 'assets/Drag';
import AddWidgetIcon from 'assets/AddWidgets';
import ErrorAlertIcon from 'assets/ErrorAlert';
import DeleteIcon from 'assets/Delete';
import SettingsIcon from '@markinson/uicomponents-v2/SvgIcons/Settings';
import {
  DragSource,
  DropTarget,
  DragSourceConnector,
  DragSourceMonitor,
  DropTargetConnector,
  DropTargetMonitor
} from 'react-dnd';
import WidgetConfigurationDialog from '../WidgetConfigurationDialog';
import {
  useDeleteDashboardWidget,
  useFetchDashboardWidgets,
  useGetDMEDashboardDetailsQueryData,
  useSaveDashboardWidgetsOrder,
  useSetDashboardLayoutWidgetsQueryData
} from 'api/dashboardMaintenanceEnquiry/dashboardMaintenanceEnquiry';
import { Inline, objectify } from 'api/utils';
import { showSnackBar } from 'components/common/SnackBars/SnackBar.hooks';
import Scrollbars from 'react-custom-scrollbars';

const ItemTypes = {
  CARD: 'dashboardCard',
};

// -----------------------------
// DRAG & DROP HANDLERS
// -----------------------------

const cardSource = {
  beginDrag(props: any) {
    return {
      id: props.id,
      column: props.column,
      card: props.card // Include entire card for restriction checks
    };
  },
};

function dragCollect(connect: DragSourceConnector, monitor: DragSourceMonitor) {
  return {
    connectDragSource: connect.dragSource(),
    isDragging: monitor.isDragging(),
  };
}

const cardTarget = {
  hover(props: any, monitor: DropTargetMonitor) {
    const draggedItem = monitor.getItem();
    const { id: draggedId, column: draggedColumn, card: draggedCard } = draggedItem;
    const { id: overId, column: targetColumn } = props;

    // Check if widget is restricted from moving between columns
    const restrictedWords = ['landscape', 'grid'];
    const isRestricted =
      restrictedWords.some((word) => draggedCard.WidgetSize?.toLowerCase().includes(word)) ||
      restrictedWords.some((word) => draggedCard.WidgetTypeIdLabel?.toLowerCase().includes(word));

    if (isRestricted && draggedColumn !== targetColumn) {
      showSnackBar({ variant: 'error', message: 'Landscape or Grid widgets cannot be moved' });

      return; // Prevent cross-column move for restricted widgets
    }

    if (draggedId !== overId) {
      props.moveCard(draggedColumn, targetColumn, draggedId, overId);
    }
  },
};

function dropCollect(connect: DropTargetConnector) {
  return {
    connectDropTarget: connect.dropTarget(),
  };
}

// -----------------------------
// DRAGGABLE CARD COMPONENT
// -----------------------------

const DraggableCard = React.memo((props: any) => {
  const {
    connectDragSource,
    connectDropTarget,
    isDragging,
    classes,
    id,
    type,
    text,
    onSettingsClick,
    onDeleteClick,
    card
  } = props;
  const opacity = isDragging ? 0.4 : 1;

  const DragHandle = connectDragSource(
    <div className={classes.draggable}>
      <DragIcon style={{ fontSize: '30px' }} />
    </div>
  );

  return connectDropTarget(
    <div style={{ opacity }}>
      <Paper className={classes.paper} square elevation={1} style={{ minHeight: '70px' }}>
        {DragHandle}
        <div className={classes.name}>{text}</div>
        <div className={classes.actions}>
          <div className={classes.actionsContainer}>
            <DeleteIcon style={{ fontSize: '30px' }} onClick={() => onDeleteClick?.(id)} />
            <SettingsIcon style={{ fontSize: '30px' }} onClick={() => onSettingsClick?.(id, type, card)} />
          </div>
          { !card?.Active && <ErrorAlertIcon style={{ fontSize: '25px', position: 'absolute', top: '-5px', right: '-11px' }} /> }
        </div>
      </Paper>
    </div>
  );
});

const DraggablePaper = DropTarget(ItemTypes.CARD, cardTarget, dropCollect)(
  DragSource(ItemTypes.CARD, cardSource, dragCollect)((props: any) => <DraggableCard {...props} />)
);

// -----------------------------
// COLUMN DROP TARGET (panel-level)
// -----------------------------

const ColumnDropZone = DropTarget(
  ItemTypes.CARD,
  {
    drop(props: any, monitor: DropTargetMonitor) {
      const item = monitor.getItem();

      // Restrict based on WidgetSize or WidgetTypeIdLabel
      const restrictedWords = ['landscape', 'grid'];
      const isRestricted =
        restrictedWords.some((word) => item.card.WidgetSize?.toLowerCase().includes(word)) ||
        restrictedWords.some((word) => item.card.WidgetTypeIdLabel?.toLowerCase().includes(word));

      if (isRestricted && item.column !== props.column) {
        showSnackBar({ variant: 'error', message: 'Landscape or Grid widgets cannot be moved' });

        return; // Prevent drop
      }

      if (item.column !== props.column) {
        props.moveCard(item.column, props.column, item.id, null); // Drop at end
      }
    },
  },
  (connect) => ({
    connectDropTarget: connect.dropTarget(),
  })
)((props: any) => props.connectDropTarget(<div style={{ width: '100%' }}>{props.children}</div>));

// -----------------------------
// EMPTY DROP ZONE
// -----------------------------

const EmptyDropZone = DropTarget(ItemTypes.CARD, cardTarget, dropCollect)((props: any) => {
  const { connectDropTarget } = props;

  return connectDropTarget(
    <div style={{
      minHeight: '300px'
    }} />
  );
});

// -----------------------------
// MAIN COMPONENT
// -----------------------------

const DashboardLayoutComponent = (props: IDashboardLayoutProperties, ref: React.Ref<IDashboardLayoutHandle>) => {
  const { classes, selectedDashboardId, selectedDashboardType, selectedDashboard } = props;

  const { setActionBar } = React.useContext(ActionBarContext);
  const [openWidgetConfigurationDialog, setOpenWidgetConfigurationDialog] = React.useState(false);
  const [selectedWidget, setSelectedWidget] = React.useState<{ id: string; type: string; card: any } | null>(null);
  const params = React.useMemo(() => new URLSearchParams(location.search), [location.search]);

  const { data: dashboardLayout } = useFetchDashboardWidgets(selectedDashboardId);
  const getDashboardDetails = useGetDMEDashboardDetailsQueryData();
  const cachedDashboardDetails = getDashboardDetails(selectedDashboardId);
  const { mutateAsync: deleteDashboardWidgetMutateAsync } = useDeleteDashboardWidget();
  const saveWidgetsOrder = useSaveDashboardWidgetsOrder();
  const setDashboardLayoutWidgetsQueryData = useSetDashboardLayoutWidgetsQueryData();

  const columnsRef = React.useRef({ left: [] as any[], right: [] as any[] });
  const saveTimerRef = React.useRef<number | null>(null);
  const selectedDashboardIdRef = React.useRef(selectedDashboardId);

  const dashboardType = React.useMemo(
    () => (cachedDashboardDetails as any)?.DashboardDetail?.inlineObject?.DashboardType ?? selectedDashboardType,
    [cachedDashboardDetails, selectedDashboardType]
  );

  const layoutType = React.useMemo(
    () => (cachedDashboardDetails as any)?.DashboardDetail?.inlineObject?.LayoutType || selectedDashboard?.LayoutType || 'S',
    [cachedDashboardDetails, selectedDashboard]
  );

  // Update refs when props change
  React.useEffect(() => {
    selectedDashboardIdRef.current = selectedDashboardId;
  }, [selectedDashboardId]);

  const [columns, setColumns] = React.useState({ left: [] as any[], right: [] as any[] });

  React.useEffect(() => {
    columnsRef.current = columns;
  }, [columns]);

  const saveOrder = React.useCallback(() => {
    const allWidgets = [...columnsRef.current.left, ...columnsRef.current.right];
    const cleanWidgets = allWidgets.map(({ id, text, type, ...rest }) => rest);

    saveWidgetsOrder.mutateAsync({
      DashboardId: selectedDashboardIdRef.current,
      DashboardWidgets: cleanWidgets,
    });
  }, [saveWidgetsOrder]);

  const saveOrderWithDebounce = React.useCallback(() => {
    if (saveTimerRef.current) clearTimeout(saveTimerRef.current);
    saveTimerRef.current = window.setTimeout(saveOrder, 500);
  }, [saveOrder]);

  React.useEffect(() => {
    if (dashboardLayout?.DashboardWidgets) {
      const widgets = Object.values(dashboardLayout?.DashboardWidgets);

      const mappedCards = widgets.map((widget: any) => ({
        ...widget,
        id: widget?.DashboardWidgetId?.toString(),
        text: widget?.WidgetIdLabel || 'Unnamed Widget',
        type: widget?.WidgetTypeIdLabel || 'Unknown',
      }));

      const left = mappedCards.filter((w) => w.LayoutPanel === 'M').sort((a, b) => a.Sequence - b.Sequence);
      const right = mappedCards.filter((w) => w.LayoutPanel === 'R').sort((a, b) => a.Sequence - b.Sequence);

      setColumns({ left, right });
    }
  }, [dashboardLayout]);

  const handleSettingsClick = (id: string, type: string, card: any) => {
    setSelectedWidget({ id, type, card });
    setOpenWidgetConfigurationDialog(true);
  };

  const handleDeleteClick = async (id: string) => {
    const response = await deleteDashboardWidgetMutateAsync({ DashboardId: selectedDashboardId, DashboardWidgetId: Number(id) });

    if (response?.Status && response?.Forms?.[0]?.MessageType === 'Success') {
      setDashboardLayoutWidgetsQueryData(Number(selectedDashboardId), {
        DashboardWidgets: response?.DashboardWidgets?.map((o) => objectify(o as Inline<any>)) || [],
        Status: response?.Status
      });
    }
  };

  const handleDialogClose = () => {
    setOpenWidgetConfigurationDialog(false);
    setSelectedWidget(null);
  };

  const moveCard = (
    sourceColumn: 'left' | 'right',
    targetColumn: 'left' | 'right',
    draggedId: string,
    overId: string | null
  ) => {
    setColumns((prevColumns) => {
      const newLeft = [...prevColumns.left];
      const newRight = [...prevColumns.right];

      const sourceArray = sourceColumn === 'left' ? newLeft : newRight;
      const targetArray = targetColumn === 'left' ? newLeft : newRight;

      const draggedIndex = sourceArray.findIndex((c) => c.id === draggedId);
      if (draggedIndex === -1) return prevColumns;
      const draggedCard = sourceArray[draggedIndex];

      if (sourceColumn !== targetColumn) {
        sourceArray.splice(draggedIndex, 1);
        draggedCard.LayoutPanel = targetColumn === 'left' ? 'M' : 'R';

        if (overId) {
          const overIndex = targetArray.findIndex((c) => c.id === overId);
          if (overIndex >= 0) {
            targetArray.splice(overIndex, 0, draggedCard);
          } else {
            targetArray.push(draggedCard);
          }
        } else {
          targetArray.push(draggedCard);
        }
      } else {
        const overIndex = sourceArray.findIndex((c) => c.id === overId);
        if (overIndex === -1) return prevColumns;
        sourceArray.splice(draggedIndex, 1);
        sourceArray.splice(overIndex, 0, draggedCard);
      }

      return {
        left: newLeft.map((c, i) => ({ ...c, Sequence: i })),
        right: newRight.map((c, i) => ({ ...c, Sequence: i })),
      };
    });

    saveOrderWithDebounce();
  };

  React.useEffect(() => {
    setActionBar({
      leftIcons: [BACK_BUTTON],
      centerIcons: [],
      rightIcons: [{ label: 'Add Widget', Icon: AddWidgetIcon, action: 'AddWidget', disabled: dashboardLayout?.System}],
    });
  }, [params, dashboardLayout]);

  React.useImperativeHandle(ref, () => ({}), []);

  React.useEffect(() => {
    return () => {
      if (saveTimerRef.current) clearTimeout(saveTimerRef.current);
    };
  }, []);

  return (
    <div className={classes.formContainer}>
      <Scrollbars>
        <Grid container spacing={8} style={{ height: '100%' }}>
          <Grid item xs={layoutType === 'D' ? 8 : 12}>
            <div className={classes.leftColumn}>
              <ColumnDropZone column='left' moveCard={moveCard}>
                <Grid container spacing={8}>
                  {columns.left.length === 0 ? (
                    <Grid item xs={12}>
                      <EmptyDropZone column='left' id='EMPTY' moveCard={moveCard} />
                    </Grid>
                  ) : (
                    columns.left.map((card) => {
                      const isLandscape = card.WidgetSize?.toLowerCase().includes('landscape');

                      return (
                        <Grid
                          key={card.id}
                          item
                          xs={isLandscape ? 12 : layoutType === 'D' ? 6 : 4}
                          className={isLandscape ? classes.fullWidthCard : classes.thirdWidthCard}
                        >
                          <DraggablePaper
                            card={card}
                            id={card.id}
                            text={card.text}
                            type={card.type}
                            classes={classes}
                            column='left'
                            moveCard={moveCard}
                            onSettingsClick={handleSettingsClick}
                            onDeleteClick={handleDeleteClick}
                          />
                        </Grid>
                      );
                    })
                  )}
                </Grid>
              </ColumnDropZone>
            </div>
          </Grid>

          {layoutType === 'D' && (
            <Grid item xs={4}>
              <div className={classes.rightColumn}>
                <ColumnDropZone column='right' moveCard={moveCard}>
                  {columns.right.length === 0 ? (
                    <EmptyDropZone column='right' id='EMPTY' moveCard={moveCard} />
                  ) : (
                    columns.right.map((card) => (
                      <DraggablePaper
                        key={card.id}
                        card={card}
                        id={card.id}
                        text={card.text}
                        type={card.type}
                        classes={classes}
                        column='right'
                        moveCard={moveCard}
                        onSettingsClick={handleSettingsClick}
                        onDeleteClick={handleDeleteClick}
                      />
                    ))
                  )}
                </ColumnDropZone>
              </div>
            </Grid>
          )}
        </Grid>
      </Scrollbars>

      <WidgetConfigurationDialog
        open={openWidgetConfigurationDialog}
        handleClose={handleDialogClose}
        selectedWidget={selectedWidget?.card}
        widgetId={selectedWidget?.id}
        widgetType={selectedWidget?.type}
        selectedDashboardType={dashboardType}
      />
    </div>
  );
};

const MemoLayout = React.memo(React.forwardRef(DashboardLayoutComponent));
export default withStyles(styles, { index: 1 })(MemoLayout);
